/**
 * Organizations repository.
 *
 * The `organizations` row drives feature flags, branding (logo, name)
 * and the AVV / DSGVO status fields. Reads are common throughout the
 * app; writes are admin-only.
 *
 * See `src/data/README.md` for the rules of the repository layer.
 */

import { supabase } from '@/integrations/supabase/client';
import { unwrap, unwrapMaybe } from '@/lib/supabase-helpers';
import type {
  Tables,
  TablesUpdate,
} from '@/integrations/supabase/types';

export type Organization = Tables<'organizations'>;
export type OrganizationUpdate = TablesUpdate<'organizations'>;

/** Fetch a single organisation by id. */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  return unwrapMaybe(
    supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .maybeSingle(),
  );
}

/**
 * Lightweight branding read used by Arztbrief / PDF generators.
 * Avoids pulling the full row when only `name` + `logo_url` are needed.
 */
export interface OrganizationBranding {
  name: string;
  logo_url: string | null;
}

export async function getOrganizationBranding(
  orgId: string,
): Promise<OrganizationBranding | null> {
  return unwrapMaybe(
    supabase
      .from('organizations')
      .select('name, logo_url')
      .eq('id', orgId)
      .maybeSingle(),
  );
}

/**
 * List every organisation. Restricted to super-admins by RLS — regular
 * therapists only see their own org.
 */
export async function listAllOrganizations() {
  return unwrap(
    supabase
      .from('organizations')
      .select('*')
      .order('name', { ascending: true }),
  );
}

/** Update an arbitrary subset of organisation fields. */
export async function updateOrganization(
  orgId: string,
  patch: OrganizationUpdate,
): Promise<Organization> {
  return unwrap(
    supabase
      .from('organizations')
      .update(patch)
      .eq('id', orgId)
      .select('*')
      .single(),
  );
}

/** Update the enabled-features list for an organisation (admin only). */
export async function setEnabledFeatures(
  orgId: string,
  features: string[],
): Promise<void> {
  await unwrap(
    supabase
      .from('organizations')
      .update({ enabled_features: features })
      .eq('id', orgId)
      .select('id')
      .single(),
  );
}

// ─── Integration-Section Helpers ─────────────────────────────────────────────
//
// The Settings → Integrationen sub-panels (HL7 ADT inbound, ISiK FHIR
// bearer, HL7 ORM outbound to clinic kitchen) all read+write a small
// fixed set of `organizations.*` columns. Each panel previously did
// the `supabase.from('organizations') as any` dance inline. The
// helpers below collapse those into typed repository calls.
//
// The columns these touch (`hl7_inbound_secret`, `isik_fhir_secret`,
// `hl7_outbound_*`) are not in the generated `Organization` type
// because `npm run types:supabase` has not been run since the
// relevant migrations. We use a single `as any` cast at the
// supabase.from boundary — same pattern as `consults.ts` /
// `encounters.ts` — and surface strong types on the application
// side via the explicit interfaces below.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const orgsTable = () => (supabase.from('organizations' as any) as any);

// HL7 v2 ADT Inbound

export async function getHl7InboundSecret(orgId: string): Promise<string | null> {
  const { data, error } = await orgsTable()
    .select('hl7_inbound_secret')
    .eq('id', orgId)
    .single();
  if (error) throw error;
  return (data as { hl7_inbound_secret: string | null } | null)?.hl7_inbound_secret ?? null;
}

export async function setHl7InboundSecret(orgId: string, secret: string | null): Promise<void> {
  const { error } = await orgsTable().update({ hl7_inbound_secret: secret }).eq('id', orgId);
  if (error) throw error;
}

// connector-inbound (universal connector hub) — own secret, deliberately
// NOT hl7_inbound_secret. See migration 20260702000003 for why: that
// column is an HMAC signing key elsewhere and must never be transmitted
// as a plain bearer value the way connector-inbound does.

export async function getConnectorInboundSecret(orgId: string): Promise<string | null> {
  const { data, error } = await orgsTable()
    .select('connector_inbound_secret')
    .eq('id', orgId)
    .single();
  if (error) throw error;
  return (data as { connector_inbound_secret: string | null } | null)?.connector_inbound_secret ?? null;
}

export async function setConnectorInboundSecret(orgId: string, secret: string | null): Promise<void> {
  const { error } = await orgsTable().update({ connector_inbound_secret: secret }).eq('id', orgId);
  if (error) throw error;
}

// ISiK FHIR Bearer

export async function getIsikFhirSecret(orgId: string): Promise<string | null> {
  const { data, error } = await orgsTable()
    .select('isik_fhir_secret')
    .eq('id', orgId)
    .single();
  if (error) throw error;
  return (data as { isik_fhir_secret: string | null } | null)?.isik_fhir_secret ?? null;
}

export async function setIsikFhirSecret(orgId: string, secret: string | null): Promise<void> {
  const { error } = await orgsTable().update({ isik_fhir_secret: secret }).eq('id', orgId);
  if (error) throw error;
}

// HL7 v2 ORM Outbound (Küchen-Integration)

export interface Hl7OutboundConfig {
  hl7_outbound_enabled: boolean;
  hl7_outbound_target_url: string | null;
  hl7_outbound_sending_facility: string | null;
  hl7_outbound_receiving_application: string | null;
  hl7_outbound_receiving_facility: string | null;
}

export async function getHl7OutboundConfig(orgId: string): Promise<Hl7OutboundConfig | null> {
  const { data, error } = await orgsTable()
    .select(
      'hl7_outbound_enabled, hl7_outbound_target_url, hl7_outbound_sending_facility, hl7_outbound_receiving_application, hl7_outbound_receiving_facility',
    )
    .eq('id', orgId)
    .single();
  if (error) throw error;
  return (data as Hl7OutboundConfig | null) ?? null;
}

export async function updateHl7OutboundConfig(
  orgId: string,
  patch: Partial<Hl7OutboundConfig>,
): Promise<void> {
  const { error } = await orgsTable().update(patch).eq('id', orgId);
  if (error) throw error;
}
